/// <amd-module name="Repos/Discussion/Components/EditComment" />
import { MarkdownPreviewWithAutocomplete, MarkdownTextFieldWithAutocomplete } from "Mention/MarkdownWithAutocomplete/Components";
import * as React from "react";
import * as Resources from "Repos/Discussion/Resources";
import { MarkdownToolbar } from "VSS/Features/MarkdownEditor/Components";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { css, KeyCode } from "VSSUI/Util";
import { IEditCommentProps } from "./EditComment.Props";

export const EditComment = (props: IEditCommentProps) => {
    const textFieldRef = React.createRef<MarkdownTextFieldWithAutocomplete>();
    const {
        author,
        buttons,
        className,
        comment,
        dirty,
        error,
        markdownEditorProps = {},
        onCancel,
        onChange,
        onError,
        onSubmit,
        personaClassName,
        personaProvider,
        personaSize = PersonaSize.size24,
        saving
    } = props;

    const messageText = markdownEditorProps.textFieldProps ? markdownEditorProps.textFieldProps.messageText : undefined;

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
        let handled = false;
        const isDirty = dirty.value;
        if (!isDirty && e.which === KeyCode.escape) {
            if (onCancel) {
                onCancel();
                handled = true;
            }
        } else if (isDirty && e.which === KeyCode.enter && e.ctrlKey) {
            if (onSubmit) {
                onSubmit();
                handled = true;
            }
        }

        if (handled) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    React.useEffect(() => {
        // On mount, set focus to the text field. Don't rely on autoFocus because
        // the browser's autofocus attribute sets initial focus in the input box with the caret at the
        // start of the content. Here, if we have content we want the cursor position to be
        // at the end of the content so that the user can continue typing.
        if (textFieldRef.current) {
            const inputElement = textFieldRef.current.getInputElement();
            if (inputElement) {
                inputElement.focus();
                if (inputElement.value) {
                    inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);
                }
            }
        }
    }, []);

    return (
        <div className={css(className, "flex-row flex-grow h-scroll-hidden rhythm-horizontal-4")}>
            <Persona
                className={css(personaClassName, "repos-comment-header-persona edit")}
                identity={author}
                personaProvider={personaProvider}
                size={personaSize}
            />
            <div className="flex-grow flex-row h-scroll-hidden padding-4">
                <div className="flex-column flex-grow rhythm-vertical-4 repos-comment-editor-fit repos-comment-editor-max-width">
                    <Observer saving={saving}>
                        {(savingProps: { saving: boolean }) => {
                            const textField = (
                                <MarkdownTextFieldWithAutocomplete
                                    {...markdownEditorProps.textFieldProps}
                                    error={error}
                                    maxLength={150000}
                                    messageText={savingProps.saving ? Resources.SavingMessageText : messageText}
                                    onChange={onChange}
                                    onError={onError}
                                    onKeyDown={onKeyDown}
                                    ref={textFieldRef}
                                    rows={2}
                                    value={comment}
                                    ariaLabel={Resources.AddComment}
                                />
                            );

                            return markdownEditorProps.wrapTextField ? markdownEditorProps.wrapTextField(textField, textFieldRef) : textField;
                        }}
                    </Observer>
                    <div className="flex-row rhythm-horizontal-8 flex-wrap justify-end">
                        <MarkdownToolbar {...markdownEditorProps.toolbarProps} className="flex-grow" getTextSource={() => textFieldRef.current} />
                        <div className="flex-row flex-wrap rhythm-horizontal-8">{buttons}</div>
                    </div>
                    <MarkdownPreviewWithAutocomplete {...markdownEditorProps.previewProps} refreshThrottleMs={1000} value={comment} />
                </div>
            </div>
        </div>
    );
};
